import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification, showSuccessNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  HrailSubstructure,
  InsertHrailSubstructureBody,
  UpdateHrailSubstructureNewImageBody,
  UpdateHrailSubstructureRemoveImageBody,
} from "../../../../../http/hrail/types/substructures";
import { useHrailSubstructuresApi } from "../../../../../http/hrail/useSubstructures";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import useImageUploader from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface SubstructureFormBody {
  name: string;
  minThicknessMm: number | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface SubstructureFormProps {
  substructure?: HrailSubstructure;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onCreated?: (id: string) => void;
  onSaved?: () => void;
}

const SubstructureForm: FC<SubstructureFormProps> = ({ substructure, isDisabled, isLoading, onClose, onCreated, onSaved }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const substructureApi = useHrailSubstructuresApi();
  const api = useApi();

  const { component, newImage, removeImage, onSave: onImageSave } = useImageUploader({
    defaultImage: substructure?.image ?? undefined,
    isLoading: !!isLoading && !substructure,
    disabled: isDisabled,
  });

  const defaultValues: SubstructureFormBody = {
    name: substructure?.name ?? "",
    minThicknessMm: substructure?.minThicknessMm ?? null,
    order: substructure?.order ?? 50,
    visibility: substructure?.visibility ?? "internal",
  };

  const onSubmit = () => async (formData: SubstructureFormBody) => {
    setIsSubmitting(true);

    const common: Omit<InsertHrailSubstructureBody, "image"> = {
      name: formData.name ?? "",
      minThicknessMm: typeof formData.minThicknessMm === "number" ? formData.minThicknessMm : null,
      order: formData.order ?? 50,
      visibility: formData.visibility,
    };

    if (substructure) {
      const update = async (body: UpdateHrailSubstructureRemoveImageBody | UpdateHrailSubstructureNewImageBody) => {
        try {
          await substructureApi.updateAdminHrailSubstructure(substructure.id, body);
          dispatch(showSuccessNotification(i18n.translation));
          onImageSave();
          onSaved?.();
          onClose?.();
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
        } finally {
          setIsSubmitting(false);
        }
      };

      if (newImage) {
        try {
          const signedUrl = await substructureApi.generateUploadImageUrl(substructure.id, newImage.name, newImage.type);
          const uploadResponse = await api.uploadFile(signedUrl, newImage);
          if (!uploadResponse.ok) {
            dispatch(showErrorNotification(i18n, uploadResponse));
            setIsSubmitting(false);
            return;
          }
          await update({
            ...common,
            image: newImage.name,
          });
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
          setIsSubmitting(false);
        }
      } else {
        await update({
          ...common,
          removeImage,
        });
      }
    } else {
      try {
        const res = await substructureApi.insertAdminHrailSubstructure({
          ...common,
          image: newImage?.name ?? null,
        });
        if (newImage) {
          const signedUrl = await substructureApi.generateUploadImageUrl(res.id, newImage.name, newImage.type);
          const uploadResponse = await api.uploadFile(signedUrl, newImage);
          if (!uploadResponse.ok) {
            dispatch(showErrorNotification(i18n, uploadResponse));
            return;
          }
        }
        dispatch(showSuccessNotification(i18n.translation));
        onImageSave();
        onCreated?.(res.id);
        onSaved?.();
        onClose?.();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<SubstructureFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        onClose ? (
          <Button buttonProps={{ onClick: onClose }} type="secondary">
            {i18n.translation.common.cancel}
          </Button>
        ) : undefined
      }
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, formState: { errors }, register, reset, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [substructure]);

        return (
          <FormFieldsContainer fullWidth imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name} isOptional={false}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading || isSubmitting}
                  {...register("name", { required: true, minLength: 1 })}
                />
              )}
            </FormField>
            <Controller
              name="minThicknessMm"
              control={control}
              rules={{ required: false, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.minThickness} error={fieldState.error} isOptional={true}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("minThicknessMm", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};



export default SubstructureForm;
